
import dayjs from "dayjs";
import { Component, Emit, Prop, Vue } from "nuxt-property-decorator";
import PelletsPriceCard from "~/components/molecules/display/pellets/PelletsPriceCard.vue";
import PelletsChartTimeRangeSelector from "~/components/molecules/form/PelletsChartTimeRangeSelector.vue";
import PelletsChartOverview from "~/components/organisms/chart/PelletsChartOverview.vue";
import type { ChartSeries } from "~/mixins/PelletsChartBase";
import type { PelletsDisplay } from "~/types/Pellets";
import {
  DEFAULT_PELLETS_CHART_MONTHS,
  PelletsChartMonths,
} from "~/utils/pelletsChart";

type ActiveProduct = "loose" | "pallet";

@Component({
  components: {
    PelletsPriceCard,
    PelletsChartOverview,
    PelletsChartTimeRangeSelector,
  },
})
export default class PelletsCurrentPriceSection extends Vue {
  @Prop({ type: String, required: true }) readonly regionLabel!: string;
  @Prop({ type: String, required: true }) readonly canvasID!: string;
  @Prop({ type: String, default: "Aktueller Pelletspreis" })
  readonly title!: string;
  @Prop({ type: String, default: "€ / 1000 Kg" })
  readonly yAxisLabel!: string;
  @Prop({ type: Number, default: DEFAULT_PELLETS_CHART_MONTHS })
  readonly monthsQuantity!: PelletsChartMonths;

  @Prop({ required: true }) readonly pellets!: PelletsDisplay;

  activeProducts: Record<ActiveProduct, boolean> = {
    loose: true,
    pallet: true,
  };

  isActive(product: ActiveProduct): boolean {
    return this.activeProducts[product];
  }

  toggleProduct(product: ActiveProduct): void {
    const isOnlyActive =
      this.activeProducts[product] &&
      Object.values(this.activeProducts).filter(Boolean).length === 1;
    if (isOnlyActive) {
      return;
    }
    this.activeProducts = {
      ...this.activeProducts,
      [product]: !this.activeProducts[product],
    };
  }

  get activeChartLabels(): string[] {
    const source =
      this.pellets.loose.series.length > 0
        ? this.pellets.loose.series
        : this.pellets.pallet.series;
    return source.map((point) => dayjs.unix(point.x).format("DD.MM.YYYY"));
  }

  get visibleLooseSeries(): ChartSeries {
    return this.isActive("loose") ? this.pellets.loose.series : [];
  }

  get visiblePalletSeries(): ChartSeries {
    return this.isActive("pallet") ? this.pellets.pallet.series : [];
  }

  get subtitle(): string {
    return `Der Pellets-Durchschnittspreis für ${this.regionLabel}`;
  }

  get looseDelta(): number {
    return (
      this.pellets.loose.price.currentPrice.value -
      this.pellets.loose.price.lastPrice.value
    );
  }

  get palletDelta(): number {
    return (
      this.pellets.pallet.price.currentPrice.value -
      this.pellets.pallet.price.lastPrice.value
    );
  }

  get looseTimestampUnix(): number {
    return Number(this.pellets.loose.price.currentPrice.changedAtUnix) || 0;
  }

  get palletTimestampUnix(): number {
    return Number(this.pellets.pallet.price.currentPrice.changedAtUnix) || 0;
  }

  @Emit("range-change")
  onRangeChange(months: PelletsChartMonths): PelletsChartMonths {
    return months;
  }
}
